import Spec from "csi/core/Spec";
import Key from "csi/core/Key";
import _ from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import React, { useContext } from "react";
import { getColorMatrixPath } from "src/ProductPresentation/PPService";
import { getSpecsForKeys } from "./attributeRule/RuleService";
import { ComponentFilterItem, convertSpecToComponentItem } from "./common";

export class AttributeSettingStore {
	private readonly _adHocSelectIds: string[];
	private readonly _preSelectIds: string[];
	private readonly _excludeItemIds: string[];
	private readonly _onClosePopup: (primaryIds: string[], secondaryIds: string[]) => void;
	@observable _primaryIds: string[];
	@observable _secondaryIds: string[];
	private readonly _types: Record<string, boolean> = {};

	/** user selected item ids */
	get adHocSelectIds(): string[] {
		return this._adHocSelectIds;
	}

	/** item ids already selected  */
	get preSelectIds(): string[] {
		return this._preSelectIds;
	}

	/** specs */
	@computed get attributeSpecs(): Record<string, Spec[]> {
		const { primaryType, secondaryType, hasExcludeItems, _excludeItemIds } = this;
		const specs: Record<string, Spec[]> = {};

		const _filterExcludeItems = (keys: Key[]): Key[] => {
			if (hasExcludeItems) {
				keys = keys.filter((key) => !_excludeItemIds.includes(key.id));
			}

			return keys;
		};

		let productKeys = Key.getKeysForType(primaryType, true);
		productKeys = _filterExcludeItems(productKeys);
		const productSpecs = getSpecsForKeys(productKeys);

		specs[primaryType] = productSpecs;

		// TO DO - clean up
		if (primaryType === "Style" || primaryType === "Material") {
			let colorKeys = Key.getKeysForType(secondaryType, true);
			colorKeys = _filterExcludeItems(colorKeys);
			const colorMatrixPath = getColorMatrixPath(primaryType);
			const colorSpecs = getSpecsForKeys(colorKeys, colorMatrixPath);

			specs[secondaryType] = colorSpecs;
		}

		return specs;
	}

	/** indicate if there's any items to exclude */
	get hasExcludeItems(): boolean {
		return !_.isEmpty(this._excludeItemIds);
	}

	/** adHoc list for primary type */
	get primaryIds(): string[] {
		return this._primaryIds;
	}

	/** sorted list of attributes from primary type node */
	@computed get primarySortedItems(): ComponentFilterItem[] {
		const { primaryType } = this;
		const items = this.attributeSpecs[primaryType].map((spec) => convertSpecToComponentItem(spec) as ComponentFilterItem);

		return _.sortBy(items, ["name"]);
	}

	/** primary type  */
	get primaryType(): string {
		return this.types[0];
	}

	/** the adHoc list for secondary type */
	get secondaryIds(): string[] {
		return this._secondaryIds;
	}

	/** sorted list of attributes from secondary type node */
	@computed get secondarySortedItems(): ComponentFilterItem[] | null {
		const { secondaryType } = this;

		if (!secondaryType) {
			return null;
		}

		const items = this.attributeSpecs[secondaryType].map((spec) => convertSpecToComponentItem(spec) as ComponentFilterItem);

		return _.sortBy(items, ["name"]);
	}

	/** the secondary type if any */
	get secondaryType(): string {
		return this.types[1];
	}

	/** the node types of the attributes  */
	get types(): string[] {
		return Object.keys(this._types);
	}

	/** callback when popup closes */
	@action closePopup = (): void => {
		const { primaryIds, secondaryIds, _onClosePopup } = this;
		_onClosePopup?.(primaryIds, secondaryIds);
	};

	@action postCheckPrimaryItems = (keys: string[]): void => {
		this._primaryIds = keys;
	};

	@action postCheckSecondaryItems = (keys: string[]): void => {
		this._secondaryIds = keys;
	};

	/**
	 * 
	 * @param preSelectIds  Ids already selected
	 * @param types types of the nodes 
	 * @param onClosePopup callback when close the popup
	 * @param excludeItemIds items that would be excluded from the overall list
	 */
	constructor(preSelectIds: string[], types: string[], onClosePopup?: (primaryIds: string[], secondaryIds: string[]) => void, excludeItemIds: string[] = []) {
		makeObservable(this);
		const { _types } = this;
		this._onClosePopup = onClosePopup;
		this._excludeItemIds = excludeItemIds;

		for (const type of types) {
			_types[type] = true;
		}

		this._preSelectIds = preSelectIds;
	}
}

export default AttributeSettingStore;

export const AttributeSettingStoreContext = React.createContext<AttributeSettingStore>(undefined);
export const useAttributeSettingStore = (): AttributeSettingStore => useContext(AttributeSettingStoreContext);